import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { getCategoryById } from '../data/companyData';
import { ArrowLeft, ArrowRight, MessageSquare } from 'lucide-react';
import { Button } from '../components/ui/button';

const CategoryPage = () => {
  const { categoryId } = useParams();
  const category = getCategoryById(categoryId);

  if (!category) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="pt-32 pb-20 text-center">
          <h1 className="text-2xl font-bold text-[#1a1a1a]">Kategori tidak ditemukan</h1>
          <Link to="/produk-layanan" className="text-[#001582] hover:underline mt-4 inline-block">
            Kembali ke Produk & Layanan
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="category-page">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-[#001582] to-[#001ea7] relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#FFC107] rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <Link 
            to="/produk-layanan" 
            className="inline-flex items-center text-white/80 hover:text-white mb-6 transition-colors"
            data-testid="back-link"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Kembali ke Produk & Layanan
          </Link>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-2 mb-4">
              <span className="w-2 h-2 bg-[#FFC107] rounded-full" />
              <span className="text-white text-sm font-semibold uppercase tracking-wider">Kategori</span>
            </div>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-white mb-4" data-testid="category-title">
              {category.title}
            </h1>
            <p className="text-slate-300 text-lg leading-relaxed max-w-3xl">
              {category.description}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Items Grid */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-slate-500 mb-8"
          >
            Menampilkan <span className="font-semibold text-[#001582]">{category.items.length}</span> item dalam kategori <span className="font-semibold text-[#001582]">{category.title}</span>
          </motion.p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {category.items.map((item, index) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className="group"
              >
                <Link 
                  to={`/produk-layanan/${category.id}/${item.id}`} 
                  data-testid={`item-card-${item.id}`}
                >
                  <div className="bg-white rounded-3xl border border-slate-100 shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden h-full flex flex-col">
                    {/* Image */}
                    <div className="relative aspect-[4/3] overflow-hidden">
                      <img 
                        src={item.image}
                        alt={item.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#001582]/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                        <span className="text-white font-medium text-sm">Lihat Detail</span>
                      </div>
                    </div>
                    
                    {/* Content */}
                    <div className="p-6 flex-1 flex flex-col">
                      <h3 className="font-heading text-lg font-bold text-[#1a1a1a] mb-3 group-hover:text-[#001582] transition-colors">
                        {item.title}
                      </h3>
                      <p className="text-slate-600 text-sm leading-relaxed mb-4 flex-1 line-clamp-3">
                        {item.description}
                      </p>
                      
                      {/* Specs Preview */}
                      {item.specs && (
                        <div className="flex flex-wrap gap-2 mb-4">
                          {item.specs.slice(0, 2).map((spec) => (
                            <span 
                              key={spec}
                              className="px-3 py-1 bg-slate-100 text-slate-600 text-xs rounded-full"
                            >
                              {spec}
                            </span>
                          ))}
                          {item.specs.length > 2 && (
                            <span className="px-3 py-1 bg-slate-100 text-slate-500 text-xs rounded-full">
                              +{item.specs.length - 2}
                            </span>
                          )}
                        </div>
                      )}
                      
                      <Button 
                        variant="outline"
                        className="w-full border-[#001582] text-[#001582] hover:bg-[#001582] hover:text-white rounded-xl"
                      >
                        Lihat Detail
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#f5f7fb]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h3 className="font-heading text-2xl md:text-3xl font-bold text-[#1a1a1a] mb-4">
            Butuh informasi lebih lanjut tentang {category.title}?
          </h3>
          <p className="text-slate-600 mb-8">
            Hubungi kami untuk konsultasi dan penawaran harga.
          </p>
          <a 
            href="https://wa.me/6285210005588" 
            target="_blank" 
            rel="noopener noreferrer"
          >
            <Button 
              size="lg"
              className="bg-gradient-to-r from-[#001582] to-[#001ea7] text-white hover:from-[#001ea7] hover:to-[#001582] font-semibold px-8 py-6 rounded-xl"
              data-testid="cta-whatsapp"
            >
              <MessageSquare className="w-5 h-5 mr-2" />
              Hubungi Kami
            </Button>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CategoryPage;
